const VIDEO_COMPRESSION_THRESHOLD = 20 * 1024 * 1024;
const MAX_LONG_EDGE = 1920;
const VIDEO_BITRATE = 4_000_000;
const AUDIO_BITRATE = 128_000;

export type MediaPreparationProgress = (fraction: number) => void;

/**
 * Compress large videos locally before upload when the browser exposes
 * WebCodecs. Unsupported codecs/devices safely fall back to the original.
 */
export async function prepareMediaFile(
  file: File,
  onProgress?: MediaPreparationProgress,
): Promise<File> {
  if (!file.type.startsWith('video/') || file.size < VIDEO_COMPRESSION_THRESHOLD) return file;
  if (typeof VideoEncoder === 'undefined' || typeof VideoDecoder === 'undefined') return file;

  try {
    const {
      ALL_FORMATS,
      BlobSource,
      BufferTarget,
      Conversion,
      Input,
      Mp4OutputFormat,
      Output,
    } = await import('mediabunny');

    const input = new Input({ formats: ALL_FORMATS, source: new BlobSource(file) });
    const target = new BufferTarget();
    const output = new Output({
      format: new Mp4OutputFormat({ fastStart: 'in-memory' }),
      target,
    });

    try {
      const conversion = await Conversion.init({
        input,
        output,
        tracks: 'primary',
        copy: false,
        tags: {},
        showWarnings: false,
        video: async (track) => {
          const width = await track.getDisplayWidth();
          const height = await track.getDisplayHeight();
          return {
            codec: 'avc',
            bitrate: VIDEO_BITRATE,
            frameRate: 30,
            keyFrameInterval: 2,
            hardwareAcceleration: 'prefer-hardware',
            ...(width >= height
              ? { width: Math.min(width, MAX_LONG_EDGE) }
              : { height: Math.min(height, MAX_LONG_EDGE) }),
          };
        },
        audio: {
          codec: 'aac',
          bitrate: AUDIO_BITRATE,
          sampleRate: 48_000,
        },
      });
      if (!conversion.isValid) return file;
      conversion.onProgress = (fraction) => onProgress?.(Math.max(0, Math.min(1, fraction)));
      await conversion.execute();
      const buffer = target.buffer;
      if (!buffer || buffer.byteLength === 0 || buffer.byteLength >= file.size * 0.95) return file;
      const stem = file.name.replace(/\.[^.]+$/, '') || 'family-video';
      return new File([buffer], `${stem}-optimized.mp4`, {
        type: 'video/mp4',
        lastModified: file.lastModified,
      });
    } finally {
      input.dispose();
    }
  } catch (error) {
    console.warn('Video optimization unavailable; uploading the original file.', error);
    return file;
  }
}

export async function prepareMediaFiles(
  files: File[],
  onProgress?: MediaPreparationProgress,
): Promise<File[]> {
  const prepared: File[] = [];
  for (let index = 0; index < files.length; index += 1) {
    prepared.push(await prepareMediaFile(files[index]!, (fraction) => {
      onProgress?.((index + fraction) / files.length);
    }));
    onProgress?.((index + 1) / files.length);
  }
  return prepared;
}
